import React, { useState, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Button from '../Elements/Button/Button';
import Input from '../Elements/Input/Input';
import Radio from '../Elements/Radio/Radio';
import { subscribeToNewsletter, changeAddressInput, applyDiscountCode } from '../../actions/checkout';
import { purifier } from '../../utils/dom';
import { translate } from '../../utils/locale';
import { isRestricted } from '../../utils/restrict';
import { fieldValidation, lowerCaseFirst } from '../../utils/string';
import s from './Newsletter.scss';

const Newsletter = ({ setup, footer, newsletter, checkout, seed, popup = false, isClosing = false, closePopup = () => {}, callback = false, location, ...props }) => {
	const [details, updateDetails] = useState({
		email: '',
		firstName: '',
		lastName: '',
	});
	const [showWholeForm, toggleWholeForm] = useState(false);
	const [customerType, updateCustomerType] = useState(setup.consumerType);
	const [sent, updateStatus] = useState(false);
	const [formErrors, updateErrors] = useState({});
	const [buttonError, toggleButtonError] = useState(false);
	let newsletters = newsletter.versions.filter((n) => (!n.countryRestrict || (n.countryRestrict && !isRestricted(setup, { selectedCountries: n.countryRestrict }))));
	let content = {};

	if (newsletters.length > 1 && (setup.consumerType !== 'private' || !popup) && setup.consumerTypeActive) {
		content = newsletters.filter((n) => n.versionId === 'default')[0];
	} else if (newsletters.length > 1) {
		// Choose A/B version
		newsletters = newsletters.filter((n) => n.versionId !== 'default');
		const choice = Math.floor((seed?.[0] || 1) * newsletters.length);
		content = newsletters[choice];
	} else if (newsletters.length === 1) {
		content = newsletters[0];
	}

	useEffect(() => {
		if (popup) {
			if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
			window.dataLayer.push({
				event: `popup_${isClosing ? 'closed' : 'viewed'}`,
				popup_version: content.versionId,
			});
		}
	}, []);

	useEffect(() => {
		toggleWholeForm(false);
	}, [location.pathname]);

	const submitNewsletterHandler = (e) => {
		e.preventDefault();

		e.currentTarget.querySelectorAll('input').forEach((el) => {
			onAddressInputChange(el.id, el.value);
		});

		const hasErrors = Object.keys(formErrors).filter((err) => formErrors[err]);

		if (!customerType) updateErrors((prevState) => ({ ...prevState, type: true }));

		if (hasErrors.length === 0 && customerType) {
			if (content.discountCode) props.applyDiscountCode({ discountCode: content.discountCode });
			let tags = [];

			if (popup) {
				if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
				window.dataLayer.push({
					event: 'popup_converted',
					popup_version: content.versionId,
				});

				if (content.versionId !== 'default') tags = [content.versionId];
			}

			props.subscribeToNewsletter({ ...details, type: customerType, tags })
				.then(() => {
					if (callback) callback(!content.discountCode);
					window.setTimeout(() => {
						if (!content.discountCode) {
							updateStatus(false);
							toggleWholeForm(false);
						}
						updateDetails({
							email: '',
							firstName: '',
							lastName: '',
						});
					}, 5000);
					return updateStatus(true);
				})
				.catch((err) => {
					console.log('DEBUG: err', err);
					return toggleButtonError(true);
				});
		}
	};

	const onCustomerTypeChange = (value) => {
		updateErrors((prevState) => ({ ...prevState, type: false }));
		updateCustomerType(value);
	};

	const onAddressInputChange = (id, value) => {
		const field = lowerCaseFirst(id.replace('newsletter', '').replace('Popup', ''));
		toggleButtonError(false);
		updateDetails((prevState) => ({
			...prevState,
			[field]: value,
		}));
		props.changeAddressInput('address', field, value);
		const isInvalid = fieldValidation(checkout?.payment?.address?.[field], field);
		updateErrors((prevState) => ({ ...prevState, [field]: isInvalid }));
	};

	const onEmailFocusHandler = () => {
		toggleWholeForm(true);
	};

	return (
		<div className={[s.newsletter, (sent ? s.newsletter__sent : null), (popup ? s.newsletter__popup : null)].join(' ')}>
			{
				sent && popup && content.discountCode ? (
					<div className={s.newsletter_inner}>
						{ content.text && <div className={s.newsletter_inner_text} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, content.thanks), {}, setup) }} /> }
						<div className={s.newsletter_inner_form_continue_footer}>
							<Button variantClasses={['button__fullWidth']} type="button" clickEventHandler={closePopup}>{translate(setup, content.continue)}</Button>
							<div dangerouslySetInnerHTML={{ __html: purifier(translate(setup, content.terms), {}, setup) }} />
						</div>
					</div>
				) : (
					<div className={s.newsletter_inner}>
						{ content.text && (!showWholeForm || !popup) && <div className={s.newsletter_inner_text} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, content.text), {}, setup) }} /> }
						<form className={[s.newsletter_inner_form, (buttonError ? s.newsletter_inner_form__error : null)].join(' ')} onSubmit={submitNewsletterHandler}>
							<Input variantClasses={['input__newsletter']} id={`newsletterEmail${popup ? 'Popup' : ''}`} type="email" value={details.email} placeholder={translate(setup, 'labelEmail')} inputChange={onAddressInputChange} inputBlur={onAddressInputChange} inputFocus={onEmailFocusHandler} isError={!!formErrors.email} />
							<div className={[s.newsletter_inner_form_continue, showWholeForm ? s.newsletter_inner_form_continue__showing : null].join(' ')}>
								<div className={s.newsletter_inner_form_continue_column}>
									<Input variantClasses={['input__newsletter']} id={`newsletterFirstName${popup ? 'Popup' : ''}`} type="text" value={details.firstName} placeholder={translate(setup, 'labelFirstName')} inputChange={onAddressInputChange} inputBlur={onAddressInputChange} isError={!!formErrors.firstName} />
									<Input variantClasses={['input__newsletter']} id={`newsletterLastName${popup ? 'Popup' : ''}`} type="text" value={details.lastName} placeholder={translate(setup, 'labelLastName')} inputChange={onAddressInputChange} inputBlur={onAddressInputChange} isError={!!formErrors.lastName} />
								</div>
								{
									!setup.consumerTypeActive && (
										<div className={s.newsletter_inner_form_continue_column}>
											<Radio variantClasses={['radio__newsletter']} id={`newsletterType${popup ? 'Popup' : ''}`} isError={formErrors.type} options={[{ key: 'consumer', value: translate(setup, 'labelCustomerTypeConsumer') }, { key: 'professional', value: translate(setup, 'labelCustomerTypeProfessional') }]} selected={customerType} inputChange={onCustomerTypeChange} />
										</div>
									)
								}
								<div className={s.newsletter_inner_form_continue_footer}>
									<Button variantClasses={[(buttonError ? 'button__error' : null), 'button__fullWidth']} type="submit">{translate(setup, content.button)}</Button>
									{ sent && <p dangerouslySetInnerHTML={{ __html: purifier(translate(setup, content.thanks), { ALLOWED_TAGS: ['#text'] }, setup) }} /> }
									{ !sent && <div dangerouslySetInnerHTML={{ __html: purifier(translate(setup, content.terms), {}, setup) }} /> }
								</div>
							</div>
						</form>
					</div>
				)
			}
		</div>
	);
};

const mapState = ({ environment, checkout, location, pageStatus }, { callback }) => ({ setup: environment.setup, newsletter: environment.newsletter, footer: environment.footer, seed: pageStatus.seed, checkout, callback, location });

export default hot(module)(connect(mapState, { subscribeToNewsletter, changeAddressInput, applyDiscountCode })(Newsletter));
